package sarka.design.skoria.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.ui.tooling.preview.Preview
import sarka.design.skoria.friends.IncomingRequest
import sarka.design.skoria.friends.OutgoingRequest
import sarka.design.skoria.presentation.ConversationListUiState
import sarka.design.skoria.presentation.FriendRow
import sarka.design.skoria.ui.composables.SkoriaPrimaryButton
import sarka.design.skoria.ui.composables.SkoriaTextField
import sarka.design.skoria.ui.theme.RCTheme

/**
 * Friends and conversations in one screen.
 *
 * You can only message friends, so a friend row is also the entry into a chat.
 * Incoming requests sit at the top so they're the first thing seen. Adding a
 * friend is a username field rather than "start a chat": a conversation can't
 * exist before the request is accepted.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ConversationListScreen(
    state: ConversationListUiState,
    onAddFriendUsernameChange: (String) -> Unit,
    onSendFriendRequest: () -> Unit,
    onAcceptRequest: (String) -> Unit,
    onDeclineRequest: (String) -> Unit,
    onCancelRequest: (String) -> Unit,
    onOpenConversation: (String) -> Unit,
    onBack: () -> Unit,
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Friends", color = RCTheme.colors.primary) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier.padding(padding).fillMaxSize().padding(horizontal = 12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SkoriaTextField(
                    hint = "Add friend by username",
                    value = state.addFriendUsername,
                    onValueChange = onAddFriendUsernameChange,
                    errorText = state.addFriendError,
                    modifier = Modifier.weight(1f),
                )
                SkoriaPrimaryButton(text = "Send request", onClick = onSendFriendRequest)
            }

            state.notice?.let { notice ->
                Spacer(Modifier.height(4.dp))
                Text(text = notice, color = RCTheme.colors.primary, fontSize = 13.sp)
            }

            if (state.error != null) {
                Spacer(Modifier.height(4.dp))
                Text(text = state.error, color = RCTheme.colors.error, fontSize = 14.sp)
            }

            Spacer(Modifier.height(12.dp))

            when {
                state.isLoading && state.friends.isEmpty() &&
                    state.incoming.isEmpty() && state.outgoing.isEmpty() ->
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }

                state.friends.isEmpty() && state.incoming.isEmpty() && state.outgoing.isEmpty() ->
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "No friends yet.\nSend a request above to get started.",
                            color = RCTheme.colors.onSurfaceVariant,
                            fontSize = 14.sp,
                        )
                    }

                else ->
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        if (state.incoming.isNotEmpty()) {
                            item(key = "incoming-header") { SectionHeader("Friend requests") }
                            items(
                                count = state.incoming.size,
                                key = { i -> "in-${state.incoming[i].fromUsername}" },
                            ) { i ->
                                IncomingRequestRow(
                                    request = state.incoming[i],
                                    onAccept = { onAcceptRequest(state.incoming[i].fromUsername) },
                                    onDecline = { onDeclineRequest(state.incoming[i].fromUsername) },
                                )
                            }
                        }

                        if (state.outgoing.isNotEmpty()) {
                            item(key = "outgoing-header") { SectionHeader("Sent requests") }
                            items(
                                count = state.outgoing.size,
                                key = { i -> "out-${state.outgoing[i].toUsername}" },
                            ) { i ->
                                OutgoingRequestRow(
                                    request = state.outgoing[i],
                                    onCancel = { onCancelRequest(state.outgoing[i].toUsername) },
                                )
                            }
                        }

                        if (state.friends.isNotEmpty()) {
                            item(key = "friends-header") { SectionHeader("Friends") }
                            items(
                                count = state.friends.size,
                                key = { i -> "friend-${state.friends[i].username}" },
                            ) { i ->
                                FriendRowItem(
                                    friend = state.friends[i],
                                    onClick = { onOpenConversation(state.friends[i].username) },
                                )
                            }
                        }
                    }
            }
        }
    }
}

@Composable
private fun SectionHeader(text: String) {
    Text(
        text = text,
        color = RCTheme.colors.onSurfaceVariant,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(top = 8.dp, bottom = 2.dp),
    )
}

@Composable
private fun IncomingRequestRow(
    request: IncomingRequest,
    onAccept: () -> Unit,
    onDecline: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = RCTheme.colors.surfaceVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = request.fromUsername,
                color = RCTheme.colors.onSurface,
                fontSize = 16.sp,
                modifier = Modifier.weight(1f),
            )
            SkoriaPrimaryButton(text = "Accept", onClick = onAccept)
            Spacer(Modifier.width(8.dp))
            OutlinedButton(onClick = onDecline) { Text("Decline") }
        }
    }
}

@Composable
private fun OutgoingRequestRow(
    request: OutgoingRequest,
    onCancel: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        colors = CardDefaults.cardColors(containerColor = RCTheme.colors.surfaceVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(request.toUsername, color = RCTheme.colors.onSurface, fontSize = 16.sp)
                Text("Pending", color = RCTheme.colors.onSurfaceVariant, fontSize = 12.sp)
            }
            TextButton(onClick = onCancel) { Text("Cancel") }
        }
    }
}

@Composable
private fun FriendRowItem(
    friend: FriendRow,
    onClick: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        colors = CardDefaults.cardColors(containerColor = RCTheme.colors.surfaceVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = friend.username,
                    color = RCTheme.colors.onSurface,
                    fontSize = 16.sp,
                    fontWeight = if (friend.unreadCount > 0) FontWeight.Bold else FontWeight.Normal,
                )
                val preview = friend.lastMessagePreview
                if (preview != null) {
                    Spacer(Modifier.height(2.dp))
                    Text(
                        text = if (friend.lastMessageFromMe) "You: $preview" else preview,
                        color = RCTheme.colors.onSurfaceVariant,
                        fontSize = 14.sp,
                        maxLines = 1,
                    )
                } else {
                    Spacer(Modifier.height(2.dp))
                    Text(
                        text = "Tap to start chatting",
                        color = RCTheme.colors.onSurfaceVariant,
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                }
            }

            if (friend.unreadCount > 0) {
                Spacer(Modifier.width(8.dp))
                Surface(
                    shape = CircleShape,
                    color = RCTheme.colors.primary,
                    modifier = Modifier.size(24.dp),
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Text(
                            text = if (friend.unreadCount > 9) "9+" else friend.unreadCount.toString(),
                            color = RCTheme.colors.onPrimary,
                            fontSize = 12.sp,
                        )
                    }
                }
            }
        }
    }
}

@Composable
@Preview
private fun ConversationListScreenPreview() {
    RCTheme {
        ConversationListScreen(
            state = ConversationListUiState(
                friends = listOf(
                    FriendRow("alice", since = 2L, lastMessagePreview = "See you then", unreadCount = 2),
                    FriendRow("bob", since = 1L, lastMessagePreview = "Thanks!", lastMessageFromMe = true),
                    FriendRow("carol", since = 3L),
                ),
                incoming = listOf(IncomingRequest("dave", 0L)),
                outgoing = listOf(OutgoingRequest("erin", 0L)),
            ),
            onAddFriendUsernameChange = {},
            onSendFriendRequest = {},
            onAcceptRequest = {},
            onDeclineRequest = {},
            onCancelRequest = {},
            onOpenConversation = {},
            onBack = {},
        )
    }
}
